import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { IconArrowLeft } from '@shared/ui/icons';
import { Select, Label, Field } from '@shared/ui/Input';
import { ConfirmDialog } from '@shared/ui/ConfirmDialog';
import { useAgentPermission } from '@entities/user';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import {
  type LogistDriver,
  type LogistOrder,
  type LogistRollingStock,
  type LogistTrip,
} from '@entities/logistics';
import {
  useListLogistOrdersQuery,
  useListLogistDictQuery,
  useListLogistSeriesQuery,
  useListLogistTripsQuery,
  useListLogistContainerTypesQuery,
  useDeleteLogistTripMutation,
} from '@shared/api';
import { LogistSeriesTemplateForm } from './LogistSeriesTemplateForm';
import { LogistTripsTable } from './LogistTripsTable';
import { LogistWeighingModal } from './LogistWeighingModal';

/**
 * ОСНОВА.Логист — конвейер серий рейсов (ТЗ AI-LOGIST-2026 §7.2) + весовой
 * контроль (§8.4). Выбор заказа → шаблон серии → генерация N рейсов одной
 * операцией → таблица рейсов с назначением водителя/ТС, статусами и вердиктом
 * весового контроля. Кнопка «Взвесить» открывает панель: запись взвешиваний
 * (брутто/тара/нетто), лимит по спецразрешению, пересчёт вердикта и блок
 * перегруза.
 */

const Wrap = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Title = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: 22px;
  font-weight: 600;
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  max-width: 820px;
`;

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const OrderField = styled(Field)`
  max-width: 520px;
`;

const NoAccessNotice = styled.div`
  opacity: 0.7;
  text-align: center;
  margin-top: 80px;
`;

const Msg = styled.div<{ $error?: boolean }>`
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme, $error }) =>
    $error ? theme.colors.status.danger : theme.colors.text.secondary};
`;

function apiErr(e: unknown): string {
  if (e && typeof e === 'object' && 'data' in e) {
    const d = (e as { data?: unknown }).data;
    if (d && typeof d === 'object' && 'error' in d) return String((d as { error: unknown }).error);
  }
  return 'ошибка запроса';
}

function orderLabel(o: LogistOrder): string {
  const cargo = o.cargo_name ?? 'заказ';
  const to = o.unloading_address ?? o.consignee_name ?? '';
  return `${cargo}${to ? ` → ${to}` : ''}${o.source_order_id ? ` (№ ${o.source_order_id})` : ''}`;
}

export function LogistTrips() {
  const { can_read, can_write } = useAgentPermission('logist');
  const { activeProjectId, setActiveProject } = useActiveProjectContext();
  const [orderId, setOrderId] = useState('');
  const [ok, setOk] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [weighTripId, setWeighTripId] = useState<string | null>(null);
  const [deletingTrip, setDeletingTrip] = useState<LogistTrip | null>(null);

  const { data: ordersData } = useListLogistOrdersQuery(
    activeProjectId ? { project_id: activeProjectId } : undefined,
  );

  useEffect(() => {
    if (!orderId) return;
    const ord = ordersData?.orders.find((o) => o.id === orderId);
    if (ord?.project_id) {
      setActiveProject(ord.project_id);
    }
  }, [orderId, ordersData?.orders, setActiveProject]);

  const { data: driversData } = useListLogistDictQuery({ dict: 'drivers', active: true });
  const { data: vehiclesData } = useListLogistDictQuery({ dict: 'vehicles', active: true });
  const { data: trailersData } = useListLogistDictQuery({ dict: 'trailers', active: true });
  const { data: tripsData } = useListLogistTripsQuery(orderId ? { order_id: orderId } : {});
  const { data: seriesData } = useListLogistSeriesQuery(
    orderId ? { order_id: orderId } : ({} as { order_id: string }),
    { skip: !orderId },
  );
  const { data: containerTypesData } = useListLogistContainerTypesQuery();

  const [deleteTrip] = useDeleteLogistTripMutation();

  const orders = ordersData?.orders ?? [];
  const drivers = (driversData?.items ?? []) as LogistDriver[];
  const vehicles = (vehiclesData?.items ?? []) as LogistRollingStock[];
  const trailers = (trailersData?.items ?? []) as LogistRollingStock[];
  const trips = tripsData?.trips ?? [];
  const series = seriesData?.series ?? [];
  const containerTypes = containerTypesData?.items ?? [];
  const weighTrip = weighTripId ? (trips.find((t) => t.id === weighTripId) ?? null) : null;

  if (!can_read) {
    return (
      <Wrap>
        <NoAccessNotice>Нет доступа к разделу «ОСНОВА.Логист».</NoAccessNotice>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Back to="/agents/logist">
        <IconArrowLeft size={16} />
        <span>ОСНОВА.Логист</span>
      </Back>
      <Title>Рейсы и серии</Title>
      <Subtitle>
        Конвейер ходок: самосвал на плече «карьер → объект» делает 5–15 однотипных рейсов за смену,
        на каждый — своя ЭТрН. Серия — шаблон для генерации сразу N рейсов. Выберите заказ, задайте
        шаблон и число ходок.
      </Subtitle>

      <Section>
        <OrderField>
          <Label>Заказ</Label>
          <Select value={orderId} onChange={(e) => setOrderId(e.target.value)}>
            <option value="">— выберите заказ —</option>
            {orders.map((o) => (
              <option key={o.id} value={o.id}>
                {orderLabel(o)}
              </option>
            ))}
          </Select>
        </OrderField>
      </Section>

      {ok && <Msg>{ok}</Msg>}
      {err && <Msg $error>{err}</Msg>}

      {orderId && can_write && (
        <LogistSeriesTemplateForm
          orderId={orderId}
          drivers={drivers}
          vehicles={vehicles}
          trailers={trailers}
          onSeriesCreated={(msg) => {
            setOk(msg);
            setErr(null);
          }}
        />
      )}

      {orderId && (
        <LogistTripsTable
          trips={trips}
          seriesCount={series.length}
          drivers={drivers}
          vehicles={vehicles}
          trailers={trailers}
          containerTypes={containerTypes}
          canWrite={can_write}
          weighTripId={weighTripId}
          onToggleWeigh={(t) => setWeighTripId((id) => (id === t.id ? null : t.id))}
          onDeleteTrip={(t) => setDeletingTrip(t)}
          onMessage={(msg, isError) => {
            if (isError) {
              setErr(msg);
            } else {
              setOk(msg);
            }
          }}
        />
      )}

      {weighTrip && (
        <LogistWeighingModal
          trip={weighTrip}
          canWrite={can_write}
          onClose={() => setWeighTripId(null)}
        />
      )}

      <ConfirmDialog
        open={!!deletingTrip}
        title="Удалить рейс"
        warning={
          deletingTrip
            ? `Рейс № ${deletingTrip.seq_no ?? '—'} и все его взвешивания будут удалены без возможности восстановления. Если по рейсу уже выпускалась накладная, удаление не пройдёт.`
            : ''
        }
        confirmText="удалить"
        confirmLabel="Введите «удалить» для подтверждения"
        onConfirm={async () => {
          if (!deletingTrip) return;
          try {
            await deleteTrip(deletingTrip.id).unwrap();
            setOk('Рейс удалён.');
          } catch (x) {
            setErr(`Не удалось: ${apiErr(x)}`);
          }
          setDeletingTrip(null);
        }}
        onCancel={() => setDeletingTrip(null)}
      />
    </Wrap>
  );
}
